import {
  Component,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { aprobarRechazarInformesPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { FieldsetModule } from 'primeng/fieldset';
import { ConfirmationService, MessageService } from 'primeng/api';
import { LoadingOverlayComponent } from '../shared/loading-overlay.component';
import {
  BusquedaEmpresaCgrDialogComponent
} from '../common/busquedas/busqueda-empresa-cgr-dialog.component';
import { EmpresaCgr } from 'src/app/domain/model/empresa/empresa-cgr';
import { BusquedaProfesionalDialogComponent } from '../common/busquedas/busqueda-profesional-dialog.component';
import { Profesional } from 'src/app/domain/model/profesional/profesional';
import { DocumentoP8Service } from 'src/app/domain/service/documento-p8/documento-p8.service';
import { DetalleInformeDialogComponent } from './detalle-informe-dialog.component';
import { AprobarRechazarInformeRepository } from 'src/app/domain/service/aprobar-rechazar-informe/aprobar-rechazar-informe-repository';
import { HttpAprobarRechazarInformeRepository } from 'src/app/infrastructure/adapters/aprobar-rechazar-informe/http-aprobar-rechazar-informe.repository';
import { InformeCumplimiento } from 'src/app/domain/model/informe/informe-cumplimiento';
import { OffsetPageResponse } from 'src/app/domain/model/common/offset-page-response.dto';
import { CausaRechazo } from 'src/app/domain/model/aprobar-rechazar-informe/causa-rechazo';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import {
  BuscarInformesEnviadosRequest,
  ResultadoOperacion,
  toInformePayload
} from 'src/app/domain/model/aprobar-rechazar-informe/aprobar-rechazar-request';
import {
  containsDangerousContent,
  sanitizeText,
  sanitizeTextarea
} from 'src/app/common/input-utils';

/**
 * Componente: "Consultar Informes Enviados Preaprobados" — HU1232313.
 *
 * Migra el legacy {@code FilterInformesEnviadosViewImpl} + {@code FilterInformesEnviadosActivity}.
 *
 * <h4>Patrones aplicados</h4>
 * - Búsqueda por POST /buscar con body + paginación por cursor opaco.
 * - Selección múltiple para operar Aprobar / Rechazar sobre varios informes.
 * - No hay endpoints "lookup por ID": todos los campos operativos viajan en el DTO del listado
 *   (commit frontend 330df87f — Ajuste Endpoints ID).
 * - Sanitización XSS/SQLi en observaciones del rechazo con {@code sanitizeTextarea}.
 * - Corrección del mojibake legacy ("Orientaciuón" → "Orden Servicio", "Envió" → "Envio").
 */
@Component({
  selector: 'app-aprobar-rechazar-informes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    DialogModule,
    DropdownModule,
    FieldsetModule,
    InputTextModule,
    TextareaModule,
    TableModule,
    TooltipModule,
    ToastModule,
    ConfirmDialogModule,
    MessageModule,
    LoadingOverlayComponent,
    BusquedaEmpresaCgrDialogComponent,
    BusquedaProfesionalDialogComponent,
    DetalleInformeDialogComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [
    MessageService,
    ConfirmationService,
    { provide: AprobarRechazarInformeRepository, useClass: HttpAprobarRechazarInformeRepository }
  ],
  template: `
    <div class="p-4 flex flex-col gap-4 max-w-full mx-auto">
      <p-toast />
      <p-confirmDialog />
      <app-loading-overlay [blocked]="cargando()" />

      <!-- Header -->
      <div class="mb-2">
        <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">
          📋 Consultar Informes Enviados Preaprobados
        </h1>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Aprobar o rechazar informes pendientes de preaprobación.
        </p>
      </div>

      <!-- Mensajes -->
      @if (mensajeInfo()) {
        <p-message severity="info" [text]="mensajeInfo()" />
      }

      <!-- Filtros -->
      <p-fieldset legend="Filtros" [toggleable]="true" styleClass="mb-2">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Orientación: col 1; col 2 queda vacía intencionalmente -->
          <div class="field sm:max-w-[14rem]">
            <label for="ordenSvc" class="block text-sm font-medium mb-1">
              Orientación de servicio
            </label>
            <input
              id="ordenSvc"
              type="text"
              pInputText
              inputmode="numeric"
              maxlength="10"
              class="w-full min-h-[44px]"
              [(ngModel)]="filtro.orientacionServicio"
              (input)="filtro.orientacionServicio = sanitizeText(filtro.orientacionServicio)"
            />
          </div>
          <div></div>

          <!-- Empresa (buscador reutilizable) -->
          <div class="field">
            <label for="empresa" class="block text-sm font-medium mb-1">
              Empresa
            </label>
            <div class="flex gap-2 items-center">
              <input
                id="empresa"
                type="text"
                pInputText
                class="w-full min-h-[44px]"
                readonly
                [ngModel]="empresaSeleccionadaTexto()"
                placeholder="Seleccione una empresa"
              />
              <button
                pButton
                type="button"
                icon="pi pi-search"
                class="p-button-outlined min-h-[44px] min-w-[44px]"
                aria-label="Buscar empresa"
                pTooltip="Buscar empresa"
                (click)="mostrarBuscadorEmpresa.set(true)"
              ></button>
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-text p-button-danger min-h-[44px] min-w-[44px]"
                aria-label="Limpiar empresa"
                pTooltip="Limpiar empresa"
                [disabled]="!empresaSeleccionada()"
                (click)="empresaSeleccionada.set(null)"
              ></button>
            </div>
          </div>

          <!-- Profesional (buscador reutilizable) -->
          <div class="field">
            <label for="profesional" class="block text-sm font-medium mb-1">
              Profesional
            </label>
            <div class="flex gap-2 items-center">
              <input
                id="profesional"
                type="text"
                pInputText
                class="w-full min-h-[44px]"
                readonly
                [ngModel]="profesionalSeleccionadoTexto()"
                placeholder="Seleccione un profesional"
              />
              <button
                pButton
                type="button"
                icon="pi pi-search"
                class="p-button-outlined min-h-[44px] min-w-[44px]"
                aria-label="Buscar profesional"
                pTooltip="Buscar profesional"
                (click)="mostrarBuscadorProfesional.set(true)"
              ></button>
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-text p-button-danger min-h-[44px] min-w-[44px]"
                aria-label="Limpiar profesional"
                pTooltip="Limpiar profesional"
                [disabled]="!profesionalSeleccionado()"
                (click)="profesionalSeleccionado.set(null)"
              ></button>
            </div>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row justify-end sm:justify-start gap-3 w-full mt-4">
          <button
            pButton
            type="button"
            label="Limpiar"
            icon="pi pi-eraser"
            class="p-button-outlined w-full sm:w-auto min-h-[44px]"
            (click)="limpiar()"
          ></button>
          <button
            pButton
            type="button"
            label="Buscar"
            icon="pi pi-search"
            class="w-full sm:w-auto min-h-[44px]"
            (click)="buscar()"
          ></button>
        </div>
      </p-fieldset>

      <!-- Tabla + Botonera -->
      @if (informes().length > 0) {
        <p-card class="mb-4">
          <ng-template pTemplate="header">
            <div class="p-3 pb-0 flex justify-between items-center flex-wrap gap-2">
              <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100 m-0">
                Informes preaprobados
              </h3>
              <div class="flex flex-col sm:flex-row gap-2 flex-wrap w-full sm:w-auto">
                <button
                  pButton
                  type="button"
                  label="Aprobar Informes"
                  icon="pi pi-check-circle"
                  class="p-button-success w-full sm:w-auto min-h-[44px]"
                  [disabled]="seleccionados().length === 0"
                  (click)="onClickAprobar()"
                ></button>
                <button
                  pButton
                  type="button"
                  label="Rechazar Informes"
                  icon="pi pi-times-circle"
                  class="p-button-danger w-full sm:w-auto min-h-[44px]"
                  [disabled]="seleccionados().length === 0"
                  (click)="abrirModalRechazo()"
                ></button>
              </div>
            </div>
          </ng-template>

          <!-- Título tabla + Registros -->
          <div class="flex justify-between items-center px-3 pb-2">
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200 m-0">
              Listado de Informes
            </h3>
            @if (totalRegistros() > 0) {
              <span class="text-sm text-gray-500 dark:text-gray-400">
                Registros del {{ registroDesde() }} hasta el {{ registroHasta() }} de {{ totalRegistros() }}
              </span>
            }
          </div>

          <!-- Vista mobile: tarjetas -->
          <div class="md:hidden space-y-3 px-3">
            @if (!cargando() && informes().length === 0) {
              <div class="text-center py-4 text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg">
                {{ informesTableConfig().state.emptyMessage }}
              </div>
            }

            @for (informe of informes(); track $index) {
              <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
                <div class="flex items-start justify-between gap-2 mb-2">
                  <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    Orden {{ textoTabla(informe.cdorden) }}
                  </div>
                  <input
                    type="checkbox"
                    class="h-5 w-5 mt-1"
                    [checked]="estaSeleccionado(informe)"
                    (change)="toggleSeleccion(informe, $any($event.target).checked)" />
                </div>

                <div class="grid grid-cols-1 gap-2 text-sm">
                  <div><span class="text-gray-500">Consultor:</span> <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dsasesor) }}</span></div>
                  <div><span class="text-gray-500">Cliente:</span> <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dsbeneficiario) }}</span></div>
                  <div><span class="text-gray-500">Servicio:</span> <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dsservicio) }}</span></div>
                  <div><span class="text-gray-500">Tarea:</span> <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dstarea) }}</span></div>
                  <div><span class="text-gray-500">Fecha envío:</span> <span class="text-gray-800 dark:text-gray-200 ml-1">{{ formatearFecha(informe.feenvio) }}</span></div>
                  <div><span class="text-gray-500">Parcial:</span> <span class="text-gray-800 dark:text-gray-200 ml-1">{{ formatearSn(informe.snparcial) }}</span></div>
                </div>
                <div class="flex flex-wrap gap-2 mt-2">
                  <button pButton label="Ver Informe" icon="pi pi-file" class="p-button-sm p-button-outlined"
                    (click)="verInforme(informe)"></button>
                  <button pButton label="Ver Detalle" icon="pi pi-eye" class="p-button-sm p-button-outlined"
                    (click)="verDetalle(informe)"></button>
                  <button pButton label="Ver PFR" icon="pi pi-book" class="p-button-sm p-button-outlined"
                    (click)="verPfr(informe)"></button>
                </div>
              </div>
            }
          </div>

          <!-- Vista desktop: tabla -->
          <div class="hidden md:block">
          <p-table
            [value]="informes()"
            [(selection)]="seleccionadosBinding"
            [dataKey]="informesTableDataKey()"
            [styleClass]="informesTableStyleClass()"
            [resizableColumns]="informesTableConfig().style.resizableColumns !== false"
            columnResizeMode="expand"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="informesTableConfig().responsive.scrollHeight || '480px'"
            [rowHover]="true"
            [tableStyle]="{ 'min-width': '72rem' }"
            [attr.aria-label]="informesTableConfig().a11y.tableAriaLabel"
            [paginator]="false"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn style="width: 3rem">
                  <p-tableHeaderCheckbox />
                </th>
                <th pResizableColumn><span class="grid-header-text" title="Orden Servicio">Orden Servicio</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Consultor">Consultor</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Cliente">Cliente</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Servicio">Servicio</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Tarea">Tarea</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Fecha Envio">Fecha Envio</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Parcial">Parcial</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Ver Informe">Ver Informe</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Ver Detalle">Ver Detalle</span></th>
                <th pResizableColumn><span class="grid-header-text" title="Ver PFR">Ver PFR</span></th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-informe>
              <tr>
                <td>
                  <p-tableCheckbox [value]="informe" />
                </td>
                <td><span class="grid-cell-text" [attr.title]="tituloTabla(informe.cdorden)">{{ textoTabla(informe.cdorden) }}</span></td>
                <td><span class="grid-cell-text" [attr.title]="tituloTabla(informe.dsasesor)">{{ textoTabla(informe.dsasesor) }}</span></td>
                <td><span class="grid-cell-text" [attr.title]="tituloTabla(informe.dsbeneficiario)">{{ textoTabla(informe.dsbeneficiario) }}</span></td>
                <td><span class="grid-cell-text" [attr.title]="tituloTabla(informe.dsservicio)">{{ textoTabla(informe.dsservicio) }}</span></td>
                <td><span class="grid-cell-text" [attr.title]="tituloTabla(informe.dstarea)">{{ textoTabla(informe.dstarea) }}</span></td>
                <td><span class="grid-cell-text" [attr.title]="formatearFecha(informe.feenvio)">{{ formatearFecha(informe.feenvio) }}</span></td>
                <td><span class="grid-cell-text" [attr.title]="formatearSn(informe.snparcial)">{{ formatearSn(informe.snparcial) }}</span></td>
                <td>
                  <button pButton icon="pi pi-file" class="p-button-sm p-button-text min-w-[44px] min-h-[44px]"
                    pTooltip="Ver Informe en P8" (click)="verInforme(informe)"></button>
                </td>
                <td>
                  <button pButton icon="pi pi-eye" class="p-button-sm p-button-text min-w-[44px] min-h-[44px]"
                    pTooltip="Ver Detalle" (click)="verDetalle(informe)"></button>
                </td>
                <td>
                  <button pButton icon="pi pi-book" class="p-button-sm p-button-text min-w-[44px] min-h-[44px]"
                    pTooltip="Ver PFR" (click)="verPfr(informe)"></button>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="11" class="text-center py-4 text-gray-500 dark:text-gray-400">
                  {{ informesTableConfig().state.emptyMessage }}
                </td>
              </tr>
            </ng-template>
          </p-table>
          </div>

          <!-- Paginación estándar (rectificar-transporte) -->
          @if (totalRegistros() > 0) {
            <div class="flex justify-between items-center p-3 border-t dark:border-gray-700 mt-2 flex-wrap gap-2">
              <div class="text-gray-600 dark:text-gray-400 text-sm">
                Página {{ currentPage() }} de {{ totalPaginas() }}
              </div>
              <div class="flex gap-2 items-center flex-wrap">
                <label class="text-sm text-gray-600 dark:text-gray-400">Registros:</label>
                <p-dropdown
                  [options]="pageSizeOptions"
                  [(ngModel)]="selectedPageSize"
                  (onChange)="onPageSizeChange()"
                  [style]="{ width: '90px' }"
                  appendTo="body"
                  scrollHeight="150px" />
                <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-double-left"
                  severity="secondary" size="small"
                  [disabled]="currentPage() <= 1"
                  (onClick)="irPrimeraPagina()" />
                <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-left"
                  severity="secondary" size="small"
                  [disabled]="!hayPaginaAnterior()"
                  (onClick)="irPaginaAnterior()" />

                <div class="flex gap-1">
                  @for (pageNum of numeroPaginasVisibles(); track $index) {
                    @if (pageNum === 0) {
                      <span class="px-2 py-1 text-gray-500 dark:text-gray-400">...</span>
                    } @else {
                      <p-button
                        [label]="pageNum.toString()"
                        severity="secondary" size="small"
                        [text]="pageNum !== currentPage()"
                        [style]="{color: pageNum === currentPage() ? '#2563eb' : ''}"
                        (onClick)="irAPagina(pageNum)" />
                    }
                  }
                </div>

                <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-right"
                  severity="secondary" size="small"
                  [disabled]="!hayPaginaSiguiente()"
                  (onClick)="irPaginaSiguiente()" />
                <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-double-right"
                  severity="secondary" size="small"
                  [disabled]="currentPage() >= totalPaginas()"
                  (onClick)="irUltimaPagina()" />
              </div>
            </div>
          }
        </p-card>
      }

      <!-- Modal Rechazar Informe -->
      <p-dialog
        header="Rechazar Informe"
        [(visible)]="modalRechazoAbierto"
        [modal]="true"
        [style]="{ width: 'min(560px, 90vw)' }"
        [dismissableMask]="false"
        [closeOnEscape]="true"
      >
        <div class="flex flex-col gap-3">
          <div class="field">
            <label class="block text-sm font-medium mb-1">
              Causa del Rechazo <span class="text-red-500">*</span>
            </label>
            <p-dropdown
              [options]="causasRechazo()"
              [(ngModel)]="modalRechazoCausaSeleccionada"
              optionLabel="dscausa"
              [filter]="true"
              filterBy="dscausa"
              placeholder="Seleccione una causa"
              appendTo="body"
              scrollHeight="150px"
              [style]="{ width: '100%' }"
            />
          </div>

          <div class="field">
            <label class="block text-sm font-medium mb-1">
              Observaciones del Rechazo <span class="text-red-500">*</span>
            </label>
            <textarea
              pTextarea
              [(ngModel)]="modalRechazoObservaciones"
              rows="5"
              maxlength="4000"
              class="w-full"
              placeholder="Detalle el motivo del rechazo (máx 4000 caracteres)."
            ></textarea>
            <small class="block text-right text-xs text-gray-500 mt-1">
              {{ modalRechazoObservaciones.length }} / 4000
            </small>
          </div>

          @if (modalRechazoError()) {
            <p-message severity="error" [text]="modalRechazoError()" />
          }
        </div>

        <ng-template pTemplate="footer">
          <div class="flex flex-col sm:flex-row justify-end gap-3 w-full">
            <button
              pButton
              type="button"
              label="Cancelar"
              icon="pi pi-times"
              class="p-button-text w-full sm:w-auto min-h-[44px]"
              (click)="modalRechazoAbierto.set(false)"
            ></button>
            <button
              pButton
              type="button"
              label="Aceptar"
              icon="pi pi-check"
              class="w-full sm:w-auto min-h-[44px]"
              (click)="confirmarRechazo()"
            ></button>
          </div>
        </ng-template>
      </p-dialog>

      <!-- Diálogos buscadores -->
      @if (mostrarBuscadorEmpresa()) {
        <app-busqueda-empresa-cgr-dialog
          (onSelect)="onEmpresaSeleccionada($event)"
          (onClose)="mostrarBuscadorEmpresa.set(false)"
        />
      }

      @if (mostrarBuscadorProfesional()) {
        <app-busqueda-profesional-dialog
          (onSelect)="onProfesionalSeleccionado($event)"
          (onClose)="mostrarBuscadorProfesional.set(false)"
        />
      }

      @if (informeDetalle()) {
        <app-detalle-informe-dialog
          [informe]="informeDetalle()!"
          (onClose)="informeDetalle.set(null)"
          (onLoaded)="cargando.set(false)"
          (onLoadingChange)="cargando.set($event)"
        />
      }
    </div>
  `,
  styles: [`
    :host ::ng-deep .tabla-aprobar-rechazar-informes .p-datatable-resizable-column {
      position: relative;
    }
    :host ::ng-deep
      .tabla-aprobar-rechazar-informes
      .p-datatable-resizable-column
      .p-datatable-column-resizer {
      cursor: col-resize;
    }
  `]
})
export class AprobarRechazarInformesComponent implements OnInit {

  private readonly repo = inject(AprobarRechazarInformeRepository);
  private readonly toast = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly documentoP8Service = inject(DocumentoP8Service);

  // ============ Estado del formulario ============
  filtro = {
    orientacionServicio: '' as string
  };
  empresaSeleccionada = signal<EmpresaCgr | null>(null);
  profesionalSeleccionado = signal<Profesional | null>(null);
  mostrarBuscadorEmpresa = signal(false);
  mostrarBuscadorProfesional = signal(false);
  informeDetalle = signal<InformeCumplimiento | null>(null);

  empresaSeleccionadaTexto = computed(() =>
    this.empresaSeleccionada()?.nombre ?? ''
  );
  profesionalSeleccionadoTexto = computed(() =>
    this.profesionalSeleccionado()?.dsnombre ?? ''
  );

  // ============ Resultados (OffsetPageResponse standard) ============
  pageResponse = signal<OffsetPageResponse<InformeCumplimiento>>({
    content: [],
    totalElements: 0,
    pageNumber: 0,
    pageSize: DEFAULT_ROWS_PER_PAGE,
    totalPages: 0,
    first: true,
    last: true,
    hasNext: false,
    hasPrevious: false
  });
  informes = computed(() => this.pageResponse().content);
  seleccionados = signal<InformeCumplimiento[]>([]);
  cargando = signal(false);
  mensajeInfo = signal<string>('');

  // Paginación 1-based en UI (currentPage = 1..N) — el backend recibe pageNumber - 1
  currentPage = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS.map(v => ({ label: String(v), value: v }));
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  totalRegistros = computed(() => this.pageResponse().totalElements);
  totalPaginas = computed(() => this.pageResponse().totalPages);
  hayPaginaSiguiente = computed(() => this.pageResponse().hasNext);
  hayPaginaAnterior = computed(() => this.pageResponse().hasPrevious);

  registroDesde = computed(() => {
    const total = this.totalRegistros();
    if (total === 0) return 0;
    return this.pageResponse().pageNumber * this.pageResponse().pageSize + 1;
  });
  registroHasta = computed(() =>
    Math.min(this.registroDesde() + this.informes().length - 1, this.totalRegistros())
  );

  numeroPaginasVisibles = computed(() => {
    const total = this.totalPaginas();
    const current = this.currentPage();
    const maxVisible = 3;

    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: number[] = [];
    const halfWindow = Math.floor(maxVisible / 2);
    let start = Math.max(1, current - halfWindow);
    let end = Math.min(total, current + halfWindow);

    if (end - start + 1 < maxVisible) {
      if (start === 1) {
        end = Math.min(total, start + maxVisible - 1);
      } else {
        start = Math.max(1, end - maxVisible + 1);
      }
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push(0);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total) {
      if (end < total - 1) pages.push(0);
      pages.push(total);
    }
    return pages;
  });

  // ============ Catálogo de causas de rechazo ============
  causasRechazo = signal<CausaRechazo[]>([]);

  // ============ Modal Rechazar ============
  modalRechazoAbierto = signal(false);
  modalRechazoCausaSeleccionada: CausaRechazo | null = null;
  modalRechazoObservaciones: string = '';
  modalRechazoError = signal<string>('');

  // ============ Binding compat para p-table [(selection)] ============
  get seleccionadosBinding(): InformeCumplimiento[] {
    return this.seleccionados();
  }
  set seleccionadosBinding(v: InformeCumplimiento[]) {
    this.seleccionados.set(v ?? []);
  }

  // ============ Configuracion estandarizada de tabla (preset) ============
  readonly informesTableConfig = computed(() =>
    aprobarRechazarInformesPreset.createConfig({
      data: this.informes() as unknown as Record<string, unknown>[],
      selection: {
        mode: 'multiple',
        selected: this.seleccionados() as unknown as Record<string, unknown>[],
        dataKey: 'nmconsecutivoinforme'
      },
      state: {
        loading: this.cargando(),
        emptyMessage: 'La búsqueda no arrojó resultados.'
      },
      a11y: {
        tableAriaLabel: 'Tabla de informes enviados preaprobados',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly informesTableStyleClass = computed(() => {
    const config = this.informesTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly informesTableDataKey = computed(() =>
    String(this.informesTableConfig().selection.dataKey || 'nmconsecutivoinforme')
  );

  async ngOnInit(): Promise<void> {
    try {
      const causas = await this.repo.listarCausasRechazo();
      this.causasRechazo.set(causas);
    } catch (err) {
      this.toast.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No se pudieron cargar las causas de rechazo. Intente nuevamente.',
      });
    }
  }

  // ============ Buscadores reutilizables ============
  onEmpresaSeleccionada(e: EmpresaCgr) {
    this.empresaSeleccionada.set(e);
    this.mostrarBuscadorEmpresa.set(false);
  }

  onProfesionalSeleccionado(p: Profesional) {
    this.profesionalSeleccionado.set(p);
    this.mostrarBuscadorProfesional.set(false);
  }

  // ============ Acciones de listado ============
  async buscar(): Promise<void> {
    this.currentPage.set(1);
    await this.ejecutarBusqueda();
  }

  limpiar(): void {
    this.filtro = { orientacionServicio: '' };
    this.empresaSeleccionada.set(null);
    this.profesionalSeleccionado.set(null);
    this.resetearPagina();
    this.seleccionados.set([]);
    this.mensajeInfo.set('');
    this.currentPage.set(1);
  }

  // ============ Paginación estándar (rectificar-transporte) ============
  irPrimeraPagina(): void { this.currentPage.set(1); this.ejecutarBusqueda(); }
  irUltimaPagina(): void { this.currentPage.set(this.totalPaginas()); this.ejecutarBusqueda(); }
  irPaginaAnterior(): void {
    if (this.hayPaginaAnterior()) { this.currentPage.update(p => p - 1); this.ejecutarBusqueda(); }
  }
  irPaginaSiguiente(): void {
    if (this.hayPaginaSiguiente()) { this.currentPage.update(p => p + 1); this.ejecutarBusqueda(); }
  }
  irAPagina(page: number): void { this.currentPage.set(page); this.ejecutarBusqueda(); }
  onPageSizeChange(): void {
    this.pageSize.set(this.selectedPageSize);
    this.currentPage.set(1);
    this.ejecutarBusqueda();
  }

  private resetearPagina(): void {
    this.pageResponse.set({
      content: [],
      totalElements: 0,
      pageNumber: 0,
      pageSize: this.pageSize(),
      totalPages: 0,
      first: true,
      last: true,
      hasNext: false,
      hasPrevious: false
    });
  }

  private async ejecutarBusqueda(): Promise<void> {
    this.cargando.set(true);
    this.mensajeInfo.set('');
    this.seleccionados.set([]);

    const request: BuscarInformesEnviadosRequest = {
      orientacionServicio: this.limpiarString(this.filtro.orientacionServicio),
      dniEmpresa: this.empresaSeleccionada()?.numeroDocumento ?? null,
      dniProfesional: this.profesionalSeleccionado()?.dni ?? null,
      pageNumber: this.currentPage() - 1,
      pageSize: this.pageSize(),
    };

    try {
      const resultado = await this.repo.buscar(request);
      this.pageResponse.set(resultado);
      if (resultado.totalElements === 0) {
        this.mensajeInfo.set('La búsqueda no arrojó resultados.');
      }
    } catch (err) {
      this.toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ocurrió un error al consultar los informes.',
      });
    } finally {
      this.cargando.set(false);
    }
  }

  // ============ Rechazo ============
  abrirModalRechazo(): void {
    if (this.seleccionados().length === 0) {
      this.toast.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No ha seleccionado ningún registro.',
      });
      return;
    }
    this.modalRechazoCausaSeleccionada = null;
    this.modalRechazoObservaciones = '';
    this.modalRechazoError.set('');
    this.modalRechazoAbierto.set(true);
  }

  async confirmarRechazo(): Promise<void> {
    const causa = this.modalRechazoCausaSeleccionada;
    const observacionesRaw = (this.modalRechazoObservaciones ?? '').trim();

    if (!causa) {
      this.modalRechazoError.set('Debe seleccionar una causa del rechazo.');
      return;
    }
    if (!observacionesRaw) {
      this.modalRechazoError.set('Debe indicar las observaciones del rechazo.');
      return;
    }
    if (containsDangerousContent(observacionesRaw)) {
      this.modalRechazoError.set('Las observaciones contienen caracteres no permitidos.');
      return;
    }

    const observaciones = sanitizeTextarea(observacionesRaw);

    this.cargando.set(true);
    try {
      const respuesta = await this.repo.rechazar({
        cdcausaRechazo: causa.cdcausaRechazo,
        dscausaRechazo: causa.dscausa,
        dsobservaciones: observaciones,
        informes: this.seleccionados().map(toInformePayload),
      });
      this.modalRechazoAbierto.set(false);
      this.notificarResultado(respuesta);
      await this.ejecutarBusqueda();
    } catch (err) {
      this.toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ocurrió un error al rechazar los informes.',
      });
    } finally {
      this.cargando.set(false);
    }
  }

  // ============ Aprobación ============
  onClickAprobar(): void {
    if (this.seleccionados().length === 0) {
      this.toast.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No ha seleccionado ningún registro.',
      });
      return;
    }
    this.confirm.confirm({
      header: 'Confirmar aprobación',
      message: '¿Está seguro que desea aprobar los informes seleccionados?',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Aceptar',
      rejectLabel: 'Cancelar',
      accept: () => this.confirmarAprobacion(),
    });
  }

  private async confirmarAprobacion(): Promise<void> {
    this.cargando.set(true);
    try {
      const respuesta = await this.repo.aprobar({
        informes: this.seleccionados().map(toInformePayload),
      });
      this.notificarResultado(respuesta);
      await this.ejecutarBusqueda();
    } catch (err) {
      this.toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ocurrió un error al aprobar los informes.',
      });
    } finally {
      this.cargando.set(false);
    }
  }

  private notificarResultado(respuesta: ResultadoOperacion): void {
    if (respuesta.exito) {
      this.toast.add({ severity: 'success', summary: 'Operación exitosa', detail: respuesta.mensaje ?? '', life: 6000 });
      return;
    }
    // Legacy: muestra el mensaje del último informe que falló
    const fallido = respuesta.resultados?.find(r => !r.exito);
    const detalle = fallido?.mensaje ?? respuesta.mensaje ?? '';
    this.toast.add({ severity: 'warn', summary: 'Operación con errores', detail: detalle, life: 8000 });
  }

  // ============ Helpers de UI ============
  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }
  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  estaSeleccionado(informe: InformeCumplimiento): boolean {
    return this.seleccionados().some(
      s => s.nmconsecutivoinforme === informe.nmconsecutivoinforme
    );
  }
  toggleSeleccion(informe: InformeCumplimiento, checked: boolean): void {
    const actual = this.seleccionados();
    if (checked) {
      if (!this.estaSeleccionado(informe)) {
        this.seleccionados.set([...actual, informe]);
      }
    } else {
      this.seleccionados.set(
        actual.filter(s => s.nmconsecutivoinforme !== informe.nmconsecutivoinforme)
      );
    }
  }
  formatearFecha(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  formatearSn(v?: string): string {
    if (!v) return '';
    if (v.toUpperCase() === 'S') return 'SI';
    if (v.toUpperCase() === 'N') return 'NO';
    return v;
  }
  // Legacy: consumirServicio(dto) — abre/descarga el documento P8 del informe
  async verInforme(informe: InformeCumplimiento): Promise<void> {
    const idP8 = informe.idP8?.trim();
    if (!idP8) {
      this.toast.add({ severity: 'warn', summary: 'Aviso', detail: 'El informe no tiene documento asociado en P8.' });
      return;
    }
    this.cargando.set(true);
    try {
      await this.documentoP8Service.descargar(idP8);
    } catch (err) {
      this.toast.add({ severity: 'error', summary: 'Error', detail: 'No fue posible descargar el informe desde P8.' });
    } finally {
      this.cargando.set(false);
    }
  }

  // Legacy: showModalDetalle(object) — modal con contactos y observaciones
  verDetalle(informe: InformeCumplimiento): void {
    this.cargando.set(true);
    this.informeDetalle.set(informe);
  }

  // Legacy: consumirServicioPanorama(dto) — abre Panoramas si tiene cdpanoramas
  async verPfr(informe: InformeCumplimiento): Promise<void> {
    const cdpanoramas = informe.cdpanoramas?.trim();
    if (!cdpanoramas) {
      this.toast.add({ severity: 'info', summary: 'Sin PFR', detail: 'No se encontraron panoramas asociados al informe.' });
      return;
    }
    this.cargando.set(true);
    try {
      const { url } = await this.repo.consultarUrlPanorama(cdpanoramas);
      window.open(url, 'Panoramas');
    } catch {
      this.toast.add({ severity: 'error', summary: 'Error',
        detail: 'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.' });
    } finally {
      this.cargando.set(false);
    }
  }

  sanitizeText = sanitizeText;
  private limpiarString(valor?: string | null): string | null {
    const t = (valor ?? '').trim();
    return t.length === 0 ? null : t;
  }
}
